import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { Locale, Media } from '@/lib/types'
import { useBackdropClose } from '@/lib/useBackdropClose'

interface LightboxProps {
  photos: Media[]
  index: number
  locale: Locale
  title: string
  onClose: () => void
  onNavigate: (index: number) => void
}

/**
 * Full-screen photo viewer.
 *
 * Written by hand rather than pulled from a library because the accessible
 * behaviour is the whole job and it is not much code: Escape closes, arrows
 * move, focus is trapped inside while it is open and returns to where it came
 * from afterwards, and the page behind does not scroll.
 */
export function Lightbox({ photos, index, locale, title, onClose, onNavigate }: LightboxProps) {
  const { t } = useTranslation()
  const backdrop = useBackdropClose(onClose)
  const dialogRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  const photo = photos[index]
  const canNavigate = photos.length > 1

  const goPrevious = useCallback(() => {
    onNavigate((index - 1 + photos.length) % photos.length)
  }, [index, photos.length, onNavigate])

  const goNext = useCallback(() => {
    onNavigate((index + 1) % photos.length)
  }, [index, photos.length, onNavigate])

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()

    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = overflow
      previouslyFocused.current?.focus()
    }
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      } else if (event.key === 'ArrowLeft' && canNavigate) {
        goPrevious()
      } else if (event.key === 'ArrowRight' && canNavigate) {
        goNext()
      } else if (event.key === 'Tab') {
        // Focus trap: with only a handful of controls, cycling them by hand is
        // simpler and more predictable than a generic trap.
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button')
        if (!focusable?.length) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last?.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first?.focus()
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canNavigate, goNext, goPrevious, onClose])

  if (!photo) return null

  const full = photo.variants.find((v) => v.label === 'full') ?? photo.variants.at(-1)
  const alt = (locale === 'uk' ? photo.altUk : photo.altEn) || t('artwork.photoOf', { title })

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-stone-950/95 backdrop-blur-sm"
      {...backdrop}
    >
      <div className="flex items-center justify-between p-4 text-stone-300">
        <span className="text-sm tabular-nums">
          {canNavigate && t('artwork.photoCounter', { current: index + 1, total: photos.length })}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-2 transition-colors hover:text-white"
        >
          <span className="sr-only">{t('artwork.closePhoto')}</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-4">
        {canNavigate && (
          <button
            type="button"
            onClick={goPrevious}
            className="mr-2 shrink-0 rounded p-3 text-stone-400 transition-colors hover:text-white"
          >
            <span className="sr-only">{t('artwork.previousPhoto')}</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        )}

        <img
          src={full?.url ?? photo.url}
          alt={alt}
          width={photo.width}
          height={photo.height}
          className="max-h-[80vh] max-w-full object-contain"
        />

        {canNavigate && (
          <button
            type="button"
            onClick={goNext}
            className="ml-2 shrink-0 rounded p-3 text-stone-400 transition-colors hover:text-white"
          >
            <span className="sr-only">{t('artwork.nextPhoto')}</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
